import {
  Button,
  Copy,
  Flex,
  Square,
  Stack,
} from '@grubhubprod/gh-cookbook-react';
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { AddNewPeopleListItem } from 'src/components/AddNewPeopleModal/AddNewPeopleListItem';
import {
  createCsvString,
  createCsvStringWithLocId,
  EmployeeListSquish,
} from 'src/components/AddNewPeopleModal/AddNewPeopleModal.utils';
import { useEmployeesToAddAnalytics } from 'src/components/AddNewPeopleModal/EmployeesToAddAnalytics';
import {
  CSVUploadStatus,
  useCSVUpload,
} from 'src/components/CSVUploadTracking/CSVUploadTracking.utils';
import { Employee } from 'src/components/EmployeeForm/EmployeeForm.utils';

export interface EmployeesToAddProps {
  onCancel: () => void;
  onUpload: (employeeCount: number) => void;
  onRemove: (email: string) => void;
  employees: Employee[];
  corpId: string;
}

export function EmployeesToAdd({
  onCancel,
  onUpload,
  onRemove,
  corpId,
  employees,
}: EmployeesToAddProps): JSX.Element {
  const { startCsvUpload, currentUploadStatus, isUploading } =
    useCSVUpload(corpId);
  const { locId } = useParams<{ locId: string }>();

  const isUploadButtonDisabled = employees.length === 0;
  const { sendAnalyticsByUploadStatus } = useEmployeesToAddAnalytics();

  const onPeopleUpload = async () => {
    const csvString = locId
      ? createCsvStringWithLocId(employees, locId as string)
      : createCsvString(employees);

    const file = new File([csvString], '*', { type: 'text/csv' });
    await startCsvUpload(file);
  };

  useEffect(() => {
    if (currentUploadStatus.status === CSVUploadStatus.COMPLETE) {
      onUpload(currentUploadStatus.totalRecords);
    }
    sendAnalyticsByUploadStatus(currentUploadStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUploadStatus, currentUploadStatus.status]);

  return (
    <>
      <EmployeeListSquish inset="md">
        {employees.length > 0 ? (
          employees.map((employee, index) => (
            <AddNewPeopleListItem
              isFirst={index === 0}
              key={employee.email}
              employee={employee}
              onRemove={onRemove}
            />
          ))
        ) : (
          <Flex alignItems="center" justifyContent="center" height="100%">
            <Copy variant="body" color="secondaryText">
              The list of new people will appear here, ready to upload
            </Copy>
          </Flex>
        )}
      </EmployeeListSquish>
      <Square as="footer" dimension="2" inset="md">
        <Flex>
          <Stack width="50%" x="x-sm">
            <Button
              block
              variant="secondary"
              buttonSize="medium"
              disabled={isUploading}
              onClick={onCancel}
            >
              Cancel
            </Button>
          </Stack>
          <Stack width="50%">
            <Button
              {...(isUploading && { 'aria-label': 'loading' })}
              block
              variant="primary"
              buttonSize="medium"
              disabled={isUploadButtonDisabled || isUploading}
              isLoading={isUploading}
              onClick={onPeopleUpload}
            >
              {`Upload new people (${employees.length})`}
            </Button>
          </Stack>
        </Flex>
      </Square>
    </>
  );
}
